import React, { useState, useRef } from 'react';
import { getImpactReportPDF } from '../apis/impact-report/report';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import PdfGenerationLoader from './PdfGenerationLoader';
import ButtonNew from '../ui/ButtonNew';

const ComponentPdfExportButton = ({
  projectTitle = 'Project Report',
  className = '',
  buttonText = 'Export as PDF',
}) => {
  const { certificateId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  console.log(certificateId, "this is certificate id in export pdf ")
  const downloadPdfFromBlob = (blob, filename) => {
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadPdfFromUrl = async (pdfUrl, projectTitle, isFirstTime = false) => {
    try {
      const filename = `${projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_impact_report.pdf`;

      // For first-time generation, use fetch + blob to avoid CORS issues
      if (isFirstTime) {
        console.log('First-time generation detected, using fetch + blob method to avoid CORS');

        try {
          const response = await fetch(pdfUrl, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit'
          });

          if (response.ok) {
            const blob = await response.blob();
            downloadPdfFromBlob(blob, filename);
            console.log('✅ First-time download successful via fetch + blob');
            return;
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        } catch (fetchError) {
          console.log('❌ Fetch method failed for first-time:', fetchError);
          // Fallback to iframe method for CORS issues
          return downloadViaIframe(pdfUrl, filename);
        }
      }

      // For instant downloads (second time), use direct anchor method
      console.log('Instant download detected, using anchor method');
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      link.target = '_blank';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, 1000);

    } catch (error) {
      console.error('Error downloading PDF from URL:', error);
      throw error;
    }
  };

  const downloadViaIframe = (pdfUrl, filename) => {
    console.log('Using iframe method as CORS fallback');

    // Create hidden iframe for download
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = pdfUrl;

    document.body.appendChild(iframe);

    // Cleanup iframe after download
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 5000);

    // Also try to open in new tab as additional fallback
    setTimeout(() => {
      window.open(pdfUrl, '_blank');
    }, 1000);
  };

  const smoothProgressUpdate = (targetProgress, isRapid = false) => {
    return new Promise((resolve) => {
      const startProgress = progressRef.current;

      // For rapid completion (100%), use fewer steps and faster intervals
      const steps = isRapid ? 5 : 20;
      const interval = isRapid ? 10 : 25; // Much faster for rapid completion

      const increment = (targetProgress - startProgress) / steps;
      let step = 0;

      const updateProgress = () => {
        step++;
        const newProgress = Math.min(startProgress + (increment * step), targetProgress);
        const roundedProgress = Math.round(newProgress);

        setProgress(roundedProgress);
        progressRef.current = roundedProgress;

        if (step < steps && newProgress < targetProgress) {
          setTimeout(updateProgress, interval);
        } else {
          setProgress(targetProgress); // Ensure we reach exact target
          progressRef.current = targetProgress;
          resolve();
        }
      };

      updateProgress();
    });
  };

  const handleExport = async () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);
    setProgress(0);
    progressRef.current = 0; // Reset progress ref to ensure we start from 0

    try {
      let pollCount = 0;
      const maxPolls = 12;
      const pollInterval = 10000; // 10 seconds

      // Initial progress for immediate feedback
      await smoothProgressUpdate(5);

      while (pollCount < maxPolls) {
        // Update progress based on poll count (5% to 85% range during polling)
        const targetProgress = Math.min(5 + ((pollCount + 1) / maxPolls) * 80, 85);
        await smoothProgressUpdate(targetProgress);

        // Make API call to backend
        const response = await getImpactReportPDF(certificateId);
        console.log(response, "see this is the response for impact report pdf");

        // Check if PDF is generated (generating is false)
        if (!response.generating) {
          console.log(response, "see this is the response for impact report pdf");

          // Determine if this is first-time generation (pollCount > 0 means we waited)
          const isFirstTime = pollCount > 0;
          console.log(`Download attempt - First time: ${isFirstTime}, Poll count: ${pollCount}`);

          // For instant downloads (pollCount === 0), show rapid progress animation
          if (pollCount === 0) {
            // Rapid progress for instant downloads
            await smoothProgressUpdate(50);
            await new Promise(resolve => setTimeout(resolve, 200)); // Small pause
            await smoothProgressUpdate(80);
            await new Promise(resolve => setTimeout(resolve, 200)); // Small pause
          }

          // Smooth progress to 90% when PDF is ready
          await smoothProgressUpdate(90);

          // Download the PDF if impactReport link is available
          if (response?.impactReport) {
            // Progress to 95% before download attempt
            await smoothProgressUpdate(100);

            // Try direct download first, fallback to window.open
            try {
              await downloadPdfFromUrl(response.impactReport, projectTitle, isFirstTime);

              // Rapid completion to 100%
              await smoothProgressUpdate(100, true);

              // Small delay to show 100% before hiding modal
              setTimeout(() => {
                setIsLoading(false);
                setProgress(0);
                progressRef.current = 0;
                toast.success('PDF exported successfully!');
              }, 500);

            } catch (downloadError) {
              console.error('Direct download failed, opening in new tab:', downloadError);
              window.open(response.impactReport, '_blank');

              // Still show rapid completion
              await smoothProgressUpdate(100, true);
              setTimeout(() => {
                setIsLoading(false);
                setProgress(0);
                progressRef.current = 0;
                toast.success('PDF opened in new tab for download!');
              }, 500);
            }
          } else {
            console.warn('PDF generation completed but no impactReport URL found');
            setIsLoading(false);
            setProgress(0);
            progressRef.current = 0;
            toast.error('PDF generation completed but download link not available');
          }
          return; // End the process
        }

        pollCount++;

        // If not the last attempt, wait before next poll
        if (pollCount < maxPolls) {
          console.log(`PDF still generating... Polling attempt ${pollCount}/${maxPolls}. Waiting 10 seconds...`);
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      }

      // If we've exhausted all polls and still generating
      console.log("PDF generation is taking longer than expected. Please try again later.");
      toast.error("PDF generation is taking longer than expected. Please try again later.");

    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    } finally {
      setIsLoading(false);
      setProgress(0);
      progressRef.current = 0;
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <ButtonNew
          type="button"
          className=''
          onClick={handleExport}
          variant="neutral"
          size="md"
        >
          <span className='flex items-center gap-2'>
            {isLoading ? (
              // Loading spinner
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-borderUnderline border-t-transparent"></div>
            ) : (
              // PDF icon
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="19" viewBox="0 0 15 19" fill="none">
                <path d="M8.7998 0.219727H1.7998C0.837305 0.219727 0.0498047 1.02973 0.0498047 2.01973V16.4197C0.0498047 17.4097 0.837305 18.2197 1.7998 18.2197H12.2998C13.2623 18.2197 14.0498 17.4097 14.0498 16.4197V5.61973L8.7998 0.219727ZM12.2998 16.4197H1.7998V2.01973H7.9248V6.51973H12.2998V16.4197ZM10.5498 8.31973V14.7097L8.7123 12.8197L6.2623 15.3397L3.8123 12.8197L6.2623 10.2997L4.3373 8.31973H10.5498Z" fill="#8C8C8C" />
              </svg>
            )}
            <span>
              {isLoading ? 'Generating PDF...' : buttonText}
            </span>
          </span>
        </ButtonNew>

      </div>

      {/* PDF Generation Loader Modal */}
      <PdfGenerationLoader isVisible={isLoading} progress={progress} />
    </>
  );
};

export default ComponentPdfExportButton;
